/**
 * Message Item Renderer
 * Renders individual messages with full formatting, media, and special cards
 */

import React, { useMemo } from 'react';
import { IonCard, IonRow, IonCol, IonIcon, IonText, IonButton, IonAvatar, IonLabel, IonItem, IonGrid } from '@ionic/react';
import { attach, call } from 'ionicons/icons';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { getIconForIdentifier, formatBusinessNotificationText } from '../../services/messageFormatting';
import styles from '../../pages/Inbox.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email' | 'call';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderStaffId?: string;
  senderType?: string;
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  wixCallRecordingBadge?: { text: string; icon?: string; iconUrl?: string };
  channelBadge?: { text: string; iconUrl?: string }; // Wix badge with logo
  contentType?: string; // FORM, TEMPLATE, TEXT, etc.
  content?: Record<string, unknown>;
  formTitle?: string; // Title for FORM messages
  emailFrom?: string; // raw "Name <email>" sender for stored emails
  /** Set only when an outgoing text could not be sent */
  deliveryStatus?: 'failed';
  subject?: string;   // email subject line
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'audio' | 'icon';
    name?: string;
    iconName?: string;
    duration?: number;
  }>;
}

interface MessageItemRenderProps {
  message: Message;
  previousMessage: Message | undefined;
  /** Retry a failed text */
  onRetry?: () => void;
  onImageClick: (image: { url?: string; type?: 'image' | 'video' | 'file' | 'audio' | 'icon'; name?: string; iconName?: string; duration?: number }) => void;
}

const formatTimestamp = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateSeparator = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const msgDate = new Date(date);
  msgDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);

  if (msgDate.getTime() === today.getTime()) {
    return i18n.t('components.chatComponents.MessageItemRender.today');
  } else if (msgDate.getTime() === yesterday.getTime()) {
    return i18n.t('components.chatComponents.MessageItemRender.yesterday');
  } else {
    return date.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }
};

const shouldShowDateSeparator = (currentMsg: Message, previousMsg: Message | undefined): boolean => {
  if (!previousMsg) return true;
  const currentDate = new Date(currentMsg._createdDate);
  const previousDate = new Date(previousMsg._createdDate);
  currentDate.setHours(0, 0, 0, 0);
  previousDate.setHours(0, 0, 0, 0);
  return currentDate.getTime() !== previousDate.getTime();
};

const renderMessageWithLinks = (text: string) => {
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlPattern);
  return parts.map((part, index) => {
    if (part.match(urlPattern)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.messageLink}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

const formatChannelLabel = (channel: string): string => {
  const normalized = channel.trim();
  if (!normalized) return '';
  if (normalized.toLowerCase() === 'sms') return i18n.t('components.chatComponents.MessageItemRender.sms');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const renderChannelBadge = (channel: string, channelBadge?: { text: string; iconUrl?: string }) => {
  // Use Wix badge with icon/logo if available
  if (channelBadge) {
    console.log(`🏷️ [renderChannelBadge] Rendering badge:`, { text: channelBadge.text, hasIcon: !!channelBadge.iconUrl });
    return (
      <IonText className={styles.channelBadge}>
        {channelBadge.iconUrl && (
          <img
            src={channelBadge.iconUrl}
            alt={channelBadge.text}
            className={styles.channelBadgeIcon}
            loading="eager"
            decoding="async"
            draggable={false}
          />
        )}
        <IonLabel>{formatChannelLabel(channelBadge.text)}</IonLabel>
      </IonText>
    );
  }

  return <IonLabel className={styles.channelBadgeFallback}>{formatChannelLabel(channel)}</IonLabel>;
};

/** Files attached to a message (an email's PDFs, documents…) as links — the bubble draws images itself, but a file would otherwise not show at all */
const renderFileLinks = (media?: Array<{ url?: string; type: string; name?: string }>) => {
  const files = (media || []).filter(m => m.type === 'file' && m.url);
  if (files.length === 0) return null;
  return files.map((file, index) => (
    <IonText key={index} className={styles.fileAttachment}>
      <p>
        <IonIcon icon={attach} />
        {' '}
        <a href={file.url} target="_blank" rel="noopener noreferrer" download={file.name}>
          {file.name || i18n.t('components.chatComponents.MessageItemRender.file')}
        </a>
      </p>
    </IonText>
  ));
};

export const MessageItemRender: React.FC<MessageItemRenderProps> = React.memo(({
  message: msg,
  previousMessage: prevMsg,
  onImageClick,
  onRetry,
}) => {
  const { t } = useTranslation();
  console.log(`🎨 [MessageItemRender] Rendering message from: ${msg.from}, avatarUrl: ${msg.avatarUrl ? 'SET' : 'UNDEFINED'}`);
  
  const showDateSeparator = useMemo(
    () => shouldShowDateSeparator(msg, prevMsg),
    [msg, prevMsg]
  );
  
  // Memoize proxied avatar URL to avoid recalculating on every render
  // PRIORITY: senderStaffAvatar (enriched staff avatar) > avatarUrl (original) 
  const proxiedAvatarUrl = useMemo(
    () => {
      const staffAvatarProxied = proxyImage(msg.senderStaffAvatar);
      const avatarUrlProxied = proxyImage(msg.avatarUrl);
      // CRITICAL: Check senderStaffAvatar FIRST before avatarUrl
      const result = staffAvatarProxied || msg.senderStaffAvatar || avatarUrlProxied || msg.avatarUrl;
      if (msg.from.includes('Helix')) {
        console.log(`🎨 DEBUG avatarUrl for ${msg.from}:`, {
          senderStaffAvatar: msg.senderStaffAvatar ? `✓ ${msg.senderStaffAvatar.substring(0, 50)}...` : '❌ MISSING',
          avatarUrl: msg.avatarUrl ? `✓ ${msg.avatarUrl.substring(0, 50)}...` : '❌ MISSING',
          staffAvatarProxied: staffAvatarProxied ? `✓ ${staffAvatarProxied.substring(0, 50)}...` : '❌ EMPTY',
          avatarUrlProxied: avatarUrlProxied ? `✓ ${avatarUrlProxied.substring(0, 50)}...` : '❌ EMPTY',
          finalResult: result ? `✓ ${result.substring(0, 50)}...` : '❌ NO AVATAR',
        });
      }
      return result;
    },
    [msg.senderStaffAvatar, msg.avatarUrl, msg.from]
  );

  // Extract form fields safely
  const formFields = (msg.content as Record<string, Record<string, Array<Record<string, string>>>>)?.form?.fields || [];
  const formTitle = msg.formTitle || msg.text || t('components.chatComponents.MessageItemRender.formSubmitted');

  return (
    <>
      {/* Date Separator */}
      {showDateSeparator && (
        <IonRow className={styles.dateSeparatorRow}>
          <IonCol size="12">
            <div className={styles.dateSeparator}>
              <span>{formatDateSeparator(msg._createdDate)}</span>
            </div>
          </IonCol>
        </IonRow>
      )}

      {/* Message */}
      <IonRow className={styles.messageRow} data-message-id={msg.id}>
        <IonCol
          size="12"
          className={msg.isFromCustomer ? styles.messageColLeft : styles.messageColRight}
        >
          <IonItem
            lines="none"
            className={`${styles.messageItem} ${msg.isFromCustomer ? styles.messageItemOther : styles.messageItemMe}`}
          >
            <IonLabel className={styles.messageLabel}>
              {msg.media && msg.media.some(m => m.type === 'icon' && m.iconName) ? (
                <>
                  {/* Business Notification - Icon and text (no message bubble) */}
                  <IonRow className={styles.sentInvoiceContainer}>
                    {msg.media.map((attachment, mediaIdx) => {
                      return attachment.type === 'icon' && attachment.iconName && (
                        <IonCol key={mediaIdx} className={styles.sentInvoiceContent}>
                          <IonIcon icon={getIconForIdentifier(attachment.iconName)} className={styles.sentInvoiceIcon} />
                          <IonText className={styles.sentInvoiceText}>{formatBusinessNotificationText(msg.text)}</IonText>
                        </IonCol>
                      );
                    })}
                  </IonRow>
                </>
              ) : msg.invoicePaidData ? (
                <>
                  {/* Invoice Paid - Card with media (left), title + amount (top right), button (bottom) */}
                  <IonCard className={styles.invoicePaidBubble}>
                    <IonRow className={styles.invoicePaidContent}>
                      <IonCol size="auto" className={styles.invoicePaidLeft}>
                        {msg.media && msg.media.map((attachment, mediaIdx) => (
                          attachment.type === 'image' && attachment.url ? (
                            <img key={mediaIdx} src={attachment.url} alt={attachment.name || t('components.chatComponents.MessageItemRender.invoice')} loading="lazy" className={styles.invoicePaidMedia} />
                          ) : attachment.type === 'icon' && attachment.iconName ? (
                            <IonIcon key={mediaIdx} icon={getIconForIdentifier(attachment.iconName)} className={styles.invoicePaidMediaIcon} />
                          ) : null
                        ))}
                      </IonCol>
                      <IonCol className={styles.invoicePaidRight}>
                        <IonText><h4 className={styles.invoicePaidTitle}>{msg.invoicePaidData.title}</h4></IonText>
                        {msg.invoicePaidData.textLines && msg.invoicePaidData.textLines.map((line, lineIdx) => (
                          <IonText key={lineIdx}><p className={styles.invoicePaidAmount}>{line}</p></IonText>
                        ))}
                      </IonCol>
                    </IonRow>
                    <IonRow className={styles.invoicePaidFooter}>
                      <IonCol>
                        <IonButton expand="full" size="small" color="primary" className={styles.viewInvoiceBtn}>
                          {msg.invoicePaidData.buttons?.[0]?.label || t('components.chatComponents.MessageItemRender.viewInvoice')}
                        </IonButton>
                      </IonCol>
                    </IonRow>
                  </IonCard>
                </>
              ) : msg.newOrderData ? (
                <>
                  {/* New Order - Card with media (left), title + details (top right), button (bottom) */}
                  <IonCard className={styles.newOrderBubble}>
                    <div className={styles.newOrderLeft}>
                      {msg.media && msg.media.map((attachment, mediaIdx) => (
                        attachment.type === 'image' && attachment.url ? (
                          <img key={mediaIdx} src={attachment.url} alt={attachment.name || t('components.chatComponents.MessageItemRender.order')} loading="lazy" className={styles.newOrderMedia} />
                        ) : attachment.type === 'icon' && attachment.iconName ? (
                          <IonIcon key={mediaIdx} icon={getIconForIdentifier(attachment.iconName)} className={styles.newOrderMediaIcon} />
                        ) : null
                      ))}
                    </div>
                    <div className={styles.newOrderRight}>
                      <IonText><h4 className={styles.newOrderTitle}>{msg.newOrderData.title}</h4></IonText>
                      {msg.newOrderData.textLines && msg.newOrderData.textLines.map((line, lineIdx) => (
                        <IonText key={lineIdx}><p className={styles.newOrderAmount}>{line}</p></IonText>
                      ))}
                    </div>
                    <div className={styles.newOrderFooter}>
                      <IonButton expand="full" size="small" color="primary" className={styles.viewOrderBtn}>
                        {msg.newOrderData.buttons?.[0]?.label || t('components.chatComponents.MessageItemRender.viewOrder')}
                      </IonButton>
                    </div>
                  </IonCard>
                </>
              ) : msg.wixCallRecordingBadge ? (
                <>
                  {/* Wix Call Recording Badge - Minimal display (like invoice sent) */}
                  <IonCard className={styles.callRecordingBadge}>
                    <IonRow className={styles.callRecordingContent}>
                      <IonCol size="auto" className={styles.callRecordingIcon}>
                        {msg.wixCallRecordingBadge.iconUrl ? (
                          <img src={msg.wixCallRecordingBadge.iconUrl} alt={t('components.chatComponents.MessageItemRender.callRecording')} className={styles.callRecordingIconImage} />
                        ) : (
                          <IonIcon icon={call} className={styles.callRecordingIconFallback} />
                        )}
                      </IonCol>
                      <IonCol className={styles.callRecordingText}>
                        <IonText><h4 className={styles.callRecordingTitle}>{msg.wixCallRecordingBadge.text}</h4></IonText>
                        <small className={styles.channelTag}>{renderChannelBadge(msg.channel, msg.channelBadge)} • {formatTimestamp(msg._createdDate)}</small>
                      </IonCol>
                    </IonRow>
                  </IonCard>
                </>
              ) : msg.contentType === 'FORM' && formFields.length > 0 ? (
                <>
                  {/* Form Submission - Display form fields in a card */}
                  <IonCard className={styles.formCard}>
                    <IonRow className={styles.formContent}>
                      <IonCol size="12">
                        <IonText><h4 className={styles.formTitle}>{formTitle}</h4></IonText>
                      </IonCol>
                    </IonRow>
                    {formFields.map((field: Record<string, string>, fieldIdx: number) => {
                      return (
                        <IonRow key={fieldIdx} className={styles.formField}>
                          <IonCol size="12">
                            <IonText><span className={styles.formFieldLabel}>{field.name.toUpperCase()}</span></IonText>
                            <IonText><span className={styles.formFieldValue}>{field.value}</span></IonText>
                          </IonCol>
                        </IonRow>
                      );
                    })}
                    <IonRow className={styles.formFooter}>
                      <IonCol>
                        <small className={styles.channelTag}>{renderChannelBadge(msg.channel, msg.channelBadge)} • {formatTimestamp(msg._createdDate)}</small>
                      </IonCol>
                    </IonRow>
                  </IonCard>
                </>
              ) : msg.media && msg.media.some(m => m.type === 'audio') ? (
                <>
                  <IonCard className={styles.audioCard}>
                    <IonRow className={styles.audioContainer}>
                      <IonCol size="12">
                        {msg.media.map((attachment, mediaIdx) => (
                          attachment.type === 'audio' && attachment.url ? (
                            <div key={mediaIdx} className={styles.audioPlayer}>
                              <audio controls className={styles.audioElement}>
                                <source src={attachment.url} type="audio/mpeg" />{t('components.chatComponents.MessageItemRender.yourBrowserDoesNotSupportThe')}
                              </audio>
                              {attachment.name && <p className={styles.audioFileName}>{attachment.name}</p>}
                            </div>
                          ) : null
                        ))}
                        {/* Text/Channel info below audio */}
                        {msg.text && msg.text.trim() && (
                          <div className={styles.audioTextContent}>
                            <p>{renderMessageWithLinks(msg.text)}</p>
                            <small className={styles.channelTag}>{renderChannelBadge(msg.channel, msg.channelBadge)} • {formatTimestamp(msg._createdDate)}</small>
                          </div>
                        )}
                        {!msg.text || !msg.text.trim() ? (
                          <small className={`${styles.channelTag} ${styles.audioChannelTagAlone}`}>
                            {renderChannelBadge(msg.channel, msg.channelBadge)} • {formatTimestamp(msg._createdDate)}
                          </small>
                        ) : null}
                      </IonCol>
                    </IonRow>
                  </IonCard>
                </>
              ) : msg.media && msg.media.length > 0 && msg.media.some(m => m.type === 'image') ? (
                <>
                  <IonCard className={styles.mediaCard}>
                    <IonRow className={styles.messageMediaSide}>
                      {msg.media.map((attachment, mediaIdx) => (
                        <IonCol key={mediaIdx} size="auto" className={styles.mediaItem}>
                          {attachment.type === 'image' && (
                            <img
                              src={attachment.url}
                              alt={attachment.name || t('components.chatComponents.MessageItemRender.image')}
                              loading="lazy"
                              className={styles.messageImage}
                              onClick={() => onImageClick(attachment)}
                            />
                          )}
                        </IonCol>
                      ))}
                    </IonRow>
                    {/* Text/Channel info below media inside card */}
                    <div className={styles.mediaTextContent}>
                      {msg.channel === 'email' && msg.subject && <p className={styles.emailSubject}><strong>{msg.subject}</strong></p>}
                      {msg.text && msg.text.trim() ? <p>{renderMessageWithLinks(msg.text)}</p> : null}
                      {renderFileLinks(msg.media)}
                      <small className={styles.channelTag}>{renderChannelBadge(msg.channel, msg.channelBadge)} • {formatTimestamp(msg._createdDate)}</small>
                    </div>
                  </IonCard>
                </>
              ) : (
                <>
                  <IonCard className={msg.isFromCustomer ? styles.messageBubbleOther : styles.messageBubbleMe}>
                    {/* Text Content with Channel & Time */}
                    <IonRow className={styles.messageContent}>
                      <IonCol className={styles.messageTextSide}>
                        {msg.channel === 'email' && msg.emailFrom && (
                          <IonText><p className={styles.emailFrom}>{t('components.chatComponents.MessageItemRender.from', { emailFrom: msg.emailFrom })}</p></IonText>
                        )}
                        {msg.channel === 'email' && msg.subject && (
                          <IonText><p className={styles.emailSubject}><strong>{msg.subject}</strong></p></IonText>
                        )}
                        <IonText>
                          <p>{msg.text && msg.text.trim() ? renderMessageWithLinks(msg.text) : null}</p>
                        </IonText>
                        {renderFileLinks(msg.media)}
                        <small className={styles.channelTag}>{renderChannelBadge(msg.channel, msg.channelBadge)} • {formatTimestamp(msg._createdDate)}</small>
                        {msg.deliveryStatus === 'failed' && (
                          <IonText color="danger" className={styles.deliveryFailed}>
                            {t('components.chatComponents.MessageItemRender.notSent')}
                            {onRetry && (
                              <IonText className={styles.deliveryRetry} role="button" tabIndex={0} onClick={onRetry}>
                                {t('components.chatComponents.MessageItemRender.retry')}
                              </IonText>
                            )}
                          </IonText>
                        )}
                      </IonCol>
                    </IonRow>
                  </IonCard>
                </>
              )}

              {/* Avatar - Show for all messages */}
              <IonGrid className={styles.messageAvatarContainer}>
                <IonText className={styles.avatarWrapper} data-tooltip={msg.senderStaffName || msg.from}>
                  <IonAvatar className={styles.messageAvatar}>
                    <img
                      src={proxiedAvatarUrl || getDefaultAvatar(msg.from)}
                      alt={msg.senderStaffName || msg.from}
                      onError={(e) => {
                        const imgElement = e.target as HTMLImageElement;
                        console.warn(`❌ Avatar image failed to load for "${msg.from}"`);
                        console.warn(`   senderStaffAvatar: ${msg.senderStaffAvatar}`);
                        console.warn(`   avatarUrl: ${msg.avatarUrl}`);
                        console.warn(`   proxiedUrl: ${proxiedAvatarUrl}`);
                        console.warn(`   attempted to load: ${(e.target as HTMLImageElement).src}`);
                        imgElement.src = getDefaultAvatar(msg.from);
                      }}
                    />
                  </IonAvatar>
                </IonText>
              </IonGrid>
            </IonLabel>
          </IonItem>
        </IonCol>
      </IonRow>
    </>
  );
});

export default MessageItemRender;
